import React, { useContext } from 'react';
import { RouterProvider, createBrowserRouter, redirect } from 'react-router-dom';
import { Full, Minimal } from '../views';
import { AppContext } from '../contexts';
import { useDataProvider } from "src/mtska-frontend-data-provider";

function pickLoggedHomePathFromObject(obj) {
    if (!obj || typeof obj !== 'object') return null;
    const raw =
        typeof obj.loggedHomePage === 'string'
            ? obj.loggedHomePage
            : typeof obj.logged_home_page === 'string'
                ? obj.logged_home_page
                : '';
    const trimmed = raw.trim();
    return trimmed || null;
}

function lookupHomepagesPath(homepages, roleKey) {
    if (!homepages || roleKey == null || roleKey === '') return undefined;
    const normalized = String(roleKey).trim();
    if (!normalized) return undefined;
    if (homepages[normalized]) return homepages[normalized];
    const found = Object.keys(homepages).find(
        (hk) => String(hk).trim().toLowerCase() === normalized.toLowerCase()
    );
    return found != null ? homepages[found] : undefined;
}

function isLikelyImpersonating(userData) {
    if (!userData) return false;
    return Boolean(
        userData.impersonating ||
        userData.is_impersonating ||
        userData.isImpersonating ||
        userData.impersonator_id != null ||
        userData.impersonatorId != null ||
        userData.impersonator != null
    );
}

function pickEffectiveRoleKeyForHome(userData) {
    const tryField = (v) => {
        if (v == null) return null;
        if (typeof v === 'string') {
            const t = v.trim();
            return t || null;
        }
        if (typeof v === 'object' && typeof v.name === 'string') {
            const t = v.name.trim();
            return t || null;
        }
        return null;
    };
    return (
        tryField(userData.activeRole) ||
        tryField(userData.active_role) ||
        tryField(userData.currentRole) ||
        tryField(userData.current_role) ||
        tryField(userData.effectiveRole) ||
        tryField(userData.effective_role) ||
        tryField(userData.impersonatedRole) ||
        tryField(userData.impersonated_role) ||
        tryField(userData.actingRole) ||
        tryField(userData.acting_role) ||
        null
    );
}

function pushUniqueRoleKey(keys, k) {
    if (k == null) return;
    const t = String(k).trim();
    if (t && !keys.includes(t)) keys.push(t);
}

/** Role keys to try on appConfiguration.homepages (order: query hint → effective → impersonated-last → primary → rest). */
function buildRoleKeysForHomeOrder(userData, roles, impersonating, queryRoleHint) {
    const keys = [];
    pushUniqueRoleKey(keys, queryRoleHint);
    pushUniqueRoleKey(keys, pickEffectiveRoleKeyForHome(userData));

    if (impersonating && Array.isArray(roles) && roles.length > 1) {
        const last = roles[roles.length - 1];
        if (typeof last === 'string') pushUniqueRoleKey(keys, last);
        else if (last && typeof last === 'object') pushUniqueRoleKey(keys, last.name);
    }

    const first = roles?.[0];
    if (typeof first === 'string') pushUniqueRoleKey(keys, first);
    else if (first && typeof first === 'object') pushUniqueRoleKey(keys, first.name);

    if (Array.isArray(roles) && roles.length > 2) {
        for (let i = 1; i < roles.length - 1; i += 1) {
            const r = roles[i];
            if (typeof r === 'string') pushUniqueRoleKey(keys, r);
            else if (r && typeof r === 'object') pushUniqueRoleKey(keys, r.name);
        }
    }

    return keys;
}

/** Any role entry (object) in roles[] or roles{} may carry loggedHomePage from the API. */
function pickLoggedHomeFromRolesContainer(roles, reverseOrder) {
    if (!roles) return null;
    let candidates = Array.isArray(roles) ? roles : Object.values(roles);
    if (reverseOrder && Array.isArray(candidates)) {
        candidates = [...candidates].reverse();
    }
    for (const r of candidates) {
        if (r && typeof r === 'object') {
            const p = pickLoggedHomePathFromObject(r);
            if (p) return p;
        }
    }
    return null;
}

/**
 * Post-login home: loggedHomePage on user, on user.role, or on user.roles[0] (object);
 * else static appConfiguration.homepages keyed by role name (or legacy roles[] string).
 */
function resolveAuthenticatedHomePath(userData, homepages, hints = {}) {
    const queryPath =
        typeof hints.homePath === 'string' && hints.homePath.trim() !== ''
            ? hints.homePath.trim()
            : null;
    if (queryPath) {
        return { path: queryPath, source: 'impersonateQueryPath' };
    }

    if (!userData) {
        return { path: null, source: 'default' };
    }

    const fromUser = pickLoggedHomePathFromObject(userData);
    if (fromUser) {
        return { path: fromUser, source: 'loggedHomePageUser' };
    }

    const fromSingularRole = pickLoggedHomePathFromObject(userData.role);
    if (fromSingularRole) {
        return { path: fromSingularRole, source: 'loggedHomePageRole' };
    }

    const roles = userData.roles;
    const impersonating = isLikelyImpersonating(userData);
    const fromAnyRole = pickLoggedHomeFromRolesContainer(roles, impersonating);
    if (fromAnyRole) {
        return { path: fromAnyRole, source: 'loggedHomePageRoles' };
    }

    const keysToTry = buildRoleKeysForHomeOrder(
        userData,
        roles,
        impersonating,
        typeof hints.homeRole === 'string' && hints.homeRole.trim() !== ''
            ? hints.homeRole.trim()
            : null
    );
    for (const roleKey of keysToTry) {
        const mapHp = lookupHomepagesPath(homepages, roleKey);
        if (mapHp) {
            return { path: mapHp, source: 'homepages' };
        }
    }
    return { path: null, source: 'default' };
}

const Routes = ({ modules = [] }) => {

    const { appConfiguration, user: contextUser } = useContext(AppContext);
    const { item } = useDataProvider('auth/token')

    const protectedLoader = ({ request }) => {
        if (!localStorage.getItem('token')) {

            const url = new URL(request.url);
            const urlSearchParams = url.searchParams.toString()
            const urlPathname = url.pathname;
            const encryptedDestination = btoa(urlPathname + '?' + urlSearchParams);

            const params = new URLSearchParams();
            params.set("dst", encryptedDestination);
            return redirect("/?" + params.toString());
        }
        return null;
    }

    const publicLoader = ({ request }) => {
        if (item?.data?.errorMessage != undefined || item?.data?.options != undefined) {
            return null
        } else if (item?.data != undefined && item?.data?.errorMessage == undefined && localStorage.getItem('token')) {
            const reqUrl = new URL(request.url);
            const sp = reqUrl.searchParams;

            if (sp.get('dst')) {
                const destination = atob(sp.get('dst'));
                return redirect(destination);
            }

            const userPayload =
                item?.data != null && contextUser != null && item.data !== contextUser
                    ? { ...contextUser, ...item.data }
                    : item?.data ?? contextUser;

            const qsParams = new URLSearchParams(sp);
            qsParams.delete('homePath');
            qsParams.delete('homeRole');
            const qs = qsParams.toString();

            const homeHints = {
                homePath: sp.get('homePath'),
                homeRole: sp.get('homeRole'),
            };
            const { path: hp } = resolveAuthenticatedHomePath(
                userPayload,
                appConfiguration.homepages,
                homeHints
            );

            if (hp) {
                return redirect(qs ? `${hp}${hp.includes('?') ? '&' : '?'}${qs}` : hp);
            } else {
                return redirect(
                    (process.env.REACT_APP_AUTH0_DEFAULT_AUTHENTICATED_URI ||
                        appConfiguration.auth0.DEFAULT_AUTHENTICATED_URI) + "?" + qs
                );
            }
        }
        return null;
    }

    const rs = [
        {
            path: "/",
            Component: Minimal,
            loader: publicLoader
        },
    ];

    //Modules in routes - se non trovi il tuo modulo al 99%:
    // • non lo hai linkato in index.js di root di mtska-frontend-modules', oppure
    // • non lo hai inserito nei moduli attivi in applications_app.modules.active
    //console.info('Cerca il tuo modulo', modules)

    Object.entries(modules).forEach(([key, m]) => {
        m.routes.map((r, j) => {
            if (r.protected) {
                r.Component = Full
                r.loader = protectedLoader
            } else {
                r.Component = Minimal
                r.loader = publicLoader
            }
            //console.info('module '+m.name, r.path)
            rs.push(r)
        })
    });

    const router = createBrowserRouter(rs);


    return <RouterProvider router={router} />
}

export default Routes;
